import type { Currency } from '@/hooks/useStorefront';

export type SupportedCurrency = Currency;

/**
 * Round to the nearest x.90. Ties round up.
 *  9.65  -> 9.90
 * 10.20  -> 9.90    (Δ 0.30 vs 0.70)
 * 10.40  -> 10.90   (tie -> up)
 * 10.60  -> 10.90
 */
export function nearestPointNinety(n: number): number {
  if (!isFinite(n) || n <= 0) return 0;
  const base = Math.floor(n);
  const lower = base - 0.10;   // (base-1).90
  const upper = base + 0.90;   // base.90
  const dLower = Math.abs(n - lower);
  const dUpper = Math.abs(n - upper);
  // tie -> upper
  const chosen = dUpper <= dLower ? upper : lower;
  return Math.round(chosen * 100) / 100;
}

/**
 * Round to the nearest whole number ending in 9. Ties round up.
 *  12 -> 9
 *  14 -> 19  (tie -> up)
 *  15 -> 19
 * 113 -> 109
 */
export function nearestIntegerEndingIn9(n: number): number {
  if (!isFinite(n) || n <= 0) return 0;
  const base = Math.round(n);
  const rest = ((base % 10) + 10) % 10;
  const lower = base - rest - 1;                // previous ...9
  const upper = base + ((9 - rest + 10) % 10);  // next ...9
  const safeLower = lower < 9 ? upper : lower;  // don't drop below 9
  const dLower = Math.abs(n - safeLower);
  const dUpper = Math.abs(n - upper);
  return dUpper <= dLower ? upper : safeLower;
}

/** Placeholder — finalized when Albania goes live. */
function nearestTen(n: number): number {
  return Math.round(n / 10) * 10;
}

export const ROUNDING_STRATEGY: Record<SupportedCurrency, (n: number) => number> = {
  CHF: (n) => n,                          // Fastpath uses CHF as-is
  EUR: (n) => nearestPointNinety(n),
  SEK: (n) => nearestIntegerEndingIn9(n),
  ALL: (n) => nearestTen(n),
};

export function roundPrice(amount: number, currency: SupportedCurrency): number {
  return ROUNDING_STRATEGY[currency](amount);
}